/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない。
 */
import { useTranslation } from 'react-i18next'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'
import { FaCircleCheckSolid } from '@enechain/ecloud-icons'

import {
  AuditCell,
  MasterDataTable,
  type MasterDataTableColumn,
  standardRowActions,
} from '~/components/common/MasterDataTable'
import { convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import { licenseTypeLabel } from '~/models/licenseType'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { isSelfWheeling } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/labels'
import { ExpiredBadge } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredBadge'
import { formatCivilDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  rows: BgMasterRow[]
  isLoading: boolean
  onEdit: (row: BgMasterRow) => void
  onDelete: (row: BgMasterRow) => void
  today: CivilDate
  /**
   * 空 state のメッセージ上書き (AC-09)。 素直に実装すると全件失効時に
   * 「データがありません」に落ちてデータ不在と誤認されるため、 呼び出し側
   * (index.tsx) が hiddenCount > 0 && rows.length === 0 のときにこの prop で
   * 非表示件数メッセージへ差し替える。
   */
  emptyLabel?: string
  /**
   * 一覧取得エラー時のメッセージ。 未指定時はエラー行を出さない。
   */
  errorLabel?: string
}

export const BgMasterTable = ({
  rows,
  isLoading,
  onEdit,
  onDelete,
  today,
  emptyLabel,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const columns: Array<MasterDataTableColumn<BgMasterRow>> = [
    // [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // design (`design/admin/occto.html` BG タブ) は 8 列目 (構成員数 と 適用開始日 の間) だが、
    // 行の状態は他の属性を読む前に分かる必要があるため一覧の左端へ統一した。
    {
      key: 'status',
      header: t('page:adminOcctoMaster.common.column.status'),
      cell: (row) => (
        <ExpiredBadge
          today={today}
          validFrom={row.validFrom}
          validTo={row.validTo}
        />
      ),
    },
    {
      key: 'bgCode',
      header: t('page:adminOcctoMaster.bgMaster.column.bgCode'),
      cell: (row) => row.bgCode,
    },
    {
      key: 'bgName',
      header: t('page:adminOcctoMaster.bgMaster.column.bgName'),
      cell: (row) => row.bgName,
    },
    // design (`design/admin/occto.html` #tab-bg thead) の列順は
    // 「…エリア → 区分…」。 エリアは固定値の短文列なので center 揃え
    // (design 埋め込み CSS `.m-table[data-tab-table="tab-bg"] th/td:nth-child(4)`、
    // design/CLAUDE.md「固定値列は TH と合わせて center」準拠)。 区分 (5 列目) は
    // design 側に center override が無いため left のまま。
    // 電源種別は design の一覧テーブルに列が無い (モーダル内のみ) ため削除。
    {
      key: 'areaCode',
      header: t('page:adminOcctoMaster.bgMaster.column.areaCode'),
      align: 'center',
      cell: (row) => convertAreaLabel(row.areaCode),
    },
    {
      key: 'licenseType',
      header: t('page:adminOcctoMaster.bgMaster.column.licenseType'),
      cell: (row) => licenseTypeLabel(row.licenseType),
    },
    {
      key: 'selfWheeling',
      header: t('page:adminOcctoMaster.bgMaster.column.selfWheeling'),
      align: 'center',
      // design は緑の単色チェックマークアイコンのみ (`--text-positive-mid` 相当)。
      // 該当しない行は低強調の「—」(design: color:var(--text-low-emphasis))。
      cell: (row) =>
        isSelfWheeling(row.licenseType) ? (
          // design (#tab-bg セルの svg) と同じく aria-hidden。 列見出し「自己託送」+
          // 行文脈で意味が伝わる (design SVG も aria-hidden="true")。
          <FaCircleCheckSolid
            boxSize="18px"
            color="Text.positiveMid"
            aria-hidden
          />
        ) : (
          <span
            style={{ color: LightModeSemanticColors.Text.lowEmphasis.value }}
          >
            —
          </span>
        ),
    },
    {
      key: 'memberCount',
      header: t('page:adminOcctoMaster.bgMaster.column.memberCount'),
      format: 'number',
      cell: (row) => row.memberCount.toLocaleString(),
    },
    {
      key: 'plantCount',
      header: t('page:adminOcctoMaster.bgMaster.column.plantCount'),
      format: 'number',
      cell: (row) => row.plantCount.toLocaleString(),
    },
    {
      key: 'validFrom',
      header: t('page:adminOcctoMaster.common.column.validFrom'),
      cell: (row) => formatCivilDate(row.validFrom),
    },
    {
      key: 'validTo',
      header: t('page:adminOcctoMaster.common.column.validTo'),
      cell: (row) =>
        row.validTo !== undefined ? formatCivilDate(row.validTo) : '—',
    },
    {
      key: 'updatedAt',
      header: t('page:adminOcctoMaster.bgMaster.column.updatedAt'),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onEdit}
      renderRowActions={standardRowActions(onEdit, onDelete)}
      rows={rows}
    />
  )
}
